import React from "react";

// Shared primitives for the Shooting Evaluation editor + results.
export function Block({ kicker, title, subtitle, testid, children, right }) {
  return (
    <section className="border border-white/10 bg-[#141414]" data-testid={testid}>
      <div className="px-5 py-4 border-b border-white/10 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.45em] text-[var(--org-accent)] font-bold">{kicker}</div>
          <h3 className="font-heading uppercase tracking-tight text-white leading-none mt-1 text-xl md:text-2xl">{title}</h3>
          {subtitle && <div className="mt-1.5 text-[10px] uppercase tracking-widest text-[#757575]">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

export function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] font-bold mb-1.5">{children}</div>;
}

export function NumField({ label, value, onChange, testid, suffix }) {
  return (
    <label className="block">
      <Label>{label}</Label>
      <div className="flex items-center gap-1">
        <input type="number" step="any" value={value ?? ""} onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}
          className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-base font-heading text-white text-center focus:outline-none focus:border-[var(--org-accent)]" data-testid={testid} />
        {suffix && <span className="text-xs text-[#757575]">{suffix}</span>}
      </div>
    </label>
  );
}

export function TextArea({ label, value, onChange, testid, placeholder, rows = 2 }) {
  return (
    <label className="block">
      {label && <Label>{label}</Label>}
      <textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
        className="w-full bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)] placeholder:text-[#575757]" data-testid={testid} />
    </label>
  );
}

// Toggle chip. `tone`: "gold" (default) | "red" | "green"
export function Chip({ active, onClick, children, testid, tone = "gold", disabled, title }) {
  const color = tone === "red" ? "#f87171" : tone === "green" ? "#4ade80" : "var(--org-accent)";
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} aria-pressed={!!active}
      className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${active ? "text-[#121212]" : "text-[#c0c0c0] hover:text-white"} ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
      style={active ? { background: color, borderColor: color } : { borderColor: "rgba(255,255,255,0.15)" }} data-testid={testid}>
      {children}
    </button>
  );
}

export function PassFail({ value, onChange, testid, disabled }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      <Chip tone="green" active={value === "Pass"} disabled={disabled} onClick={() => onChange(value === "Pass" ? "" : "Pass")} testid={`${testid}-pass`}>Pass</Chip>
      <Chip tone="red" active={value === "Breaks down"} disabled={disabled} onClick={() => onChange(value === "Breaks down" ? "" : "Breaks down")} testid={`${testid}-breaks`}>Breaks down</Chip>
    </div>
  );
}

export function Note({ children, testid }) {
  return <div className="border-l-2 border-[var(--org-accent)]/60 pl-3 text-xs text-[#c0c0c0] leading-relaxed" data-testid={testid}>{children}</div>;
}

export function LevelBadge({ L, levelId, testid }) {
  const item = L.itemById[levelId];
  return (
    <div className="inline-flex items-center gap-2 border border-[var(--org-accent)] px-3 py-1.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold">Shooting Level</span>
      <span className="font-heading uppercase text-white text-base">{item?.name || levelId}</span>
    </div>
  );
}
